<template>
    <!-- The card is a section of its own and not a bubble: what a bubble carries is
         something Milo SAID, and this is something the guest is about to do. -->
    <div class="milo-card" :class="cardClass">
        <h3>{{ operation.summary }}</h3>

        <ul v-if="operation.preview.length">
            <li v-for="(line, index) in operation.preview" :key="index">{{ line }}</li>
        </ul>

        <!-- Two buttons with words on them. "Ne, zdaj ne" and not an ✕: a guest
             declining a proposal is answering a question, not dismissing a dialog. -->
        <div v-if="decidable" class="milo-card-buttons">
            <button class="primary" :disabled="deciding" @click="emit('decide', true)">
                {{ deciding ? $t('milo.thinking') : $t('milo.confirm') }}
            </button>
            <button class="secondary" :disabled="deciding" @click="emit('decide', false)">
                {{ $t('milo.decline') }}
            </button>
        </div>

        <!-- What the press did, on the card the thumb came from. A toast would be gone
             by the time the guest looked back at it. -->
        <p v-else-if="operation.status !== 'proposed'" class="milo-card-note" :class="tone">{{ label }}</p>

        <!-- The two refusals with codes of their own (plan §5). They are shown BESIDE
             the outcome above and not instead of it: a second press on a card that was
             already confirmed has to say both that this press did nothing and what the
             first one did. -->
        <p v-if="note" class="milo-card-note">{{ $t(note) }}</p>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { guestOperationResult } from '@/libs/chatResult'
    import type { IGuestChatOperation } from '@/types/Chat'

    /**
     * One proposal: the preview the service built, and the two buttons that decide it.
     *
     * The card switches on nothing but `status`. The summary and the preview lines are
     * built by the service from parameters it has already validated — a price from the
     * catalogue and not from the model, a room number it looked up — which is why a
     * fifth kind of proposal needs no change in this file at all.
     */
    const props = defineProps<{
        operation: IGuestChatOperation
        /** An i18n key the last press left behind (expired, already decided), or ''. */
        note:      string
        deciding:  boolean
    }>()

    const emit = defineEmits<{ decide: [confirm: boolean] }>()

    const { t } = useI18n()

    const decidable = computed((): boolean => props.operation.status === 'proposed')

    const cardClass = computed((): string => {
        if (props.operation.status === 'executed') return 'done'

        return props.operation.status === 'proposed' ? '' : 'off'
    })

    const tone = computed((): string => {
        if (props.operation.status === 'executed') return 'ok'

        return props.operation.status === 'failed' ? 'bad' : ''
    })

    /**
     * The sentence after the press.
     *
     * "Oddano" and never "narejeno", deliberately: every one of the four actions is a
     * REQUEST the hotel then acts on — a late checkout the desk decides, a room the
     * housekeeper reaches, a message a person answers. A card that said "done" would be
     * this portal promising on the hotel's behalf.
     *
     * What the execution actually did comes from its structured outcome, put into the
     * guest's language here (libs/chatResult.ts) — never from the English line the
     * service wrote for its own audit trail.
     */
    const label = computed((): string => {
        const result = guestOperationResult(props.operation)

        if (props.operation.status === 'executed') return t('milo.executed') + (result ? ` — ${result}` : '')
        if (props.operation.status === 'failed') return t('milo.failed') + (result ? ` — ${result}` : '')

        return t('milo.declined')
    })
</script>
